/**
 * Data + state for the Storage admin tab.
 *
 * Owns: per-org usage (+ single-org fallback for org_admin), grand totals,
 * paginated file browser with filters, live bucket-stats scan, single-file
 * delete, orphan cleanup, quota edits.
 *
 * Extracted verbatim from StorageFilesTab.jsx during the Phase 6 refactor
 * (no behaviour change).
 */
import { useEffect, useState, useMemo, useCallback } from 'react';
import { toast } from 'sonner';
import { useApi } from './useApi';
import { useAuth } from '../contexts/AuthContext';
import { formatBytes } from '../utils/uploadImage';

export function useStorageAdmin() {
  const { get, del, post, put } = useApi();
  const { user, impersonatingRole } = useAuth();
  const realRole = user?.role || 'trainer';
  const effectiveRole = impersonatingRole || realRole;
  const isSuperAdmin = realRole === 'super_admin';
  const canDelete = ['super_admin', 'admin', 'org_admin'].includes(effectiveRole);

  const [usage, setUsage] = useState([]);
  const [grandTotal, setGrandTotal] = useState({ bytes: 0, files: 0 });
  const [files, setFiles] = useState([]);
  const [filesTotal, setFilesTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Live bucket stats (S3 list_objects_v2 against Tigris itself, super_admin only).
  // null = not loaded yet, false = forbidden / not super_admin, object = data.
  const [bucketStats, setBucketStats] = useState(null);
  const [bucketStatsLoading, setBucketStatsLoading] = useState(false);

  // Unreferenced-objects REPORT (super_admin only) — read-only diagnostic.
  // sweepPreview: null = not run, object = report result. (No delete — removed
  // May 2026 after a shared-bucket deletion incident.)
  const [sweepPreview, setSweepPreview] = useState(null);
  const [sweepLoading, setSweepLoading] = useState(false);

  // Filters
  const [orgFilter, setOrgFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [orphansOnly, setOrphansOnly] = useState(false);
  const [skip, setSkip] = useState(0);

  // Dialogs
  const [pendingDelete, setPendingDelete] = useState(null);
  const [orphanConfirm, setOrphanConfirm] = useState(false);

  const loadUsage = useCallback(async () => {
    try {
      const res = await get('/storage/usage/all');
      setUsage(res.orgs || []);
      setGrandTotal({ bytes: res.grand_total_bytes || 0, files: res.grand_total_files || 0 });
    } catch (err) {
      // org_admin can't access /usage/all (platform_admin+ only). Fall back to single-org.
      try {
        const own = await get('/storage/usage');
        setUsage([own]);
        setGrandTotal({ bytes: own.total_bytes || 0, files: own.file_count || 0 });
      } catch (innerErr) {
        console.error('Failed to load storage usage:', innerErr);
      }
    }
  }, [get]);

  const loadFiles = useCallback(async () => {
    const params = new URLSearchParams({ limit: '50', skip: String(skip) });
    if (orgFilter !== 'all') params.set('org_id', orgFilter);
    if (typeFilter !== 'all') params.set('content_type', typeFilter);
    if (search) params.set('search', search);
    if (orphansOnly) params.set('orphans_only', 'true');
    try {
      const res = await get(`/storage/files?${params.toString()}`);
      setFiles(res.files || []);
      setFilesTotal(res.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load files');
    }
  }, [get, skip, orgFilter, typeFilter, search, orphansOnly]);

  // Live bucket stats — only super_admin can call this endpoint. We don't
  // auto-load on first render because it's a multi-second S3 scan; the
  // user explicitly clicks "Verify with bucket" to trigger it.
  const loadBucketStats = useCallback(async () => {
    if (!isSuperAdmin) return;
    setBucketStatsLoading(true);
    try {
      const res = await get('/storage/bucket-stats');
      setBucketStats(res);
    } catch (err) {
      if (err.response?.status === 403) {
        setBucketStats(false);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to scan bucket');
      }
    } finally {
      setBucketStatsLoading(false);
    }
  }, [get, isSuperAdmin]);

  // Unreferenced-objects report — non-destructive scan.
  const runSweepPreview = useCallback(async () => {
    if (!isSuperAdmin) return;
    setSweepLoading(true);
    try {
      const res = await post('/storage/bucket-sweep/preview');
      setSweepPreview(res);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setSweepLoading(false);
    }
  }, [post, isSuperAdmin]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadUsage(), loadFiles()]).finally(() => setLoading(false));
  }, [loadUsage, loadFiles]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  const handleSearch = () => { setSkip(0); loadFiles(); };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await del(`/storage/files/${pendingDelete.file_id}`);
      toast.success(`Deleted "${pendingDelete.filename}"`);
      setPendingDelete(null);
      await Promise.all([loadFiles(), loadUsage()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const handleOrphanCleanup = async () => {
    setBusy(true);
    try {
      const res = await post('/storage/orphans/cleanup');
      toast.success(`Cleaned ${res.deleted_count} orphan${res.deleted_count === 1 ? '' : 's'} · freed ${formatBytes(res.bytes_freed)}`);
      setOrphanConfirm(false);
      await Promise.all([loadFiles(), loadUsage()]);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cleanup failed');
    } finally {
      setBusy(false);
    }
  };

  const handleQuotaSave = async (orgId, gb) => {
    try {
      await put(`/storage/orgs/${orgId}/quota`, { quota_gb: gb });
      toast.success('Quota updated');
      await loadUsage();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Quota update failed');
      throw e;
    }
  };

  // For type filter dropdown
  const allTypes = useMemo(() => {
    const set = new Set();
    usage.forEach(u => u.by_content_type?.forEach(c => set.add(c.content_type)));
    return Array.from(set).sort();
  }, [usage]);

  const myOrg = usage.find(o => o.org_id === user?.org_id) || usage[0];

  return {
    isSuperAdmin, canDelete,
    usage, grandTotal, files, filesTotal, loading, busy,
    bucketStats, bucketStatsLoading,
    sweepPreview, sweepLoading,
    orgFilter, setOrgFilter, typeFilter, setTypeFilter,
    search, setSearch, orphansOnly, setOrphansOnly, skip, setSkip,
    pendingDelete, setPendingDelete, orphanConfirm, setOrphanConfirm,
    loadUsage, loadFiles, loadBucketStats,
    runSweepPreview,
    handleSearch, handleDelete, handleOrphanCleanup, handleQuotaSave,
    allTypes, myOrg,
  };
}
